import {observer} from 'mobx-react';
import {FC, useCallback, useMemo} from 'react';
import {castArray, filter, isMatch, pullAllWith, transform} from 'lodash';
import {Form, Table} from 'semantic-ui-react';
import {FormFragment, durationRenderer} from 'apstra-ui-common';
import {runInAction} from 'mobx';

import {descriptionRenderer} from '../descriptionRenderer';
import {unitsRenderer} from './UnitsInput';
import {graphAnnotationPropertiesRenderer} from './GraphAnnotationPropertiesInput';
import {getOutputNameByStageName, getStageFormSchema} from '../stageUtils';
import {useMobxComputed} from '../../hooks/useMobxComputed';
import {MarkdownDescription} from './MarkdownDescription';
import {ProbeProcessor, ProbeStage, ProcessorDefinition} from '../types';
import {ProbeError} from './ProbeDetails/types';

type StageEditorProps = {
  actionInProgress?: boolean;
  errors?: ProbeError[];
  stage: ProbeStage;
  processor: ProbeProcessor;
  processorDefinition: ProcessorDefinition;
}

export const StageEditor: FC<StageEditorProps> = observer(({
  actionInProgress, errors, processor, processorDefinition, stage
}) => {
  const schema = useMobxComputed(([processor, processorDefinition, stage]) => {
    const outputName = getOutputNameByStageName({processor, stageName: stage.name})!;
    const stageDefinition = processorDefinition.outputs[outputName];
    return getStageFormSchema(stage, stageDefinition);
  }, [processor, processorDefinition, stage]);

  const errorMessagesByProperty = useMemo(() => {
    const propertyErrors = filter(errors, {type: 'stageProperty', stageName: stage.name});
    return transform(propertyErrors, (result, {propertyName, message}) => {
      if (!result[propertyName!]) result[propertyName!] = [];
      result[propertyName!].push(...castArray(message));
    }, {});
  }, [errors, stage.name]);

  const onPropertyChange = useCallback((propertyName, value) => runInAction(() => {
    pullAllWith(errors ?? [], [{type: 'stageProperty', stageName: stage.name, propertyName}], isMatch);
    stage[propertyName] = value;
  }), [errors, stage]);

  return (
    <Table size='small'>
      <Table.Header>
        <Table.Row>
          <Table.HeaderCell>{'Properties'}</Table.HeaderCell>
        </Table.Row>
      </Table.Header>
      <Table.Body>
        <Table.Row>
          <Table.Cell>
            <Form>
              <FormFragment
                schema={schema}
                values={stage}
                renderers={[
                  descriptionRenderer,
                  durationRenderer,
                  unitsRenderer,
                  graphAnnotationPropertiesRenderer,
                ]}
                errors={errorMessagesByProperty}
                disabled={actionInProgress}
                onChange={onPropertyChange}
                fieldProps={{
                  DescriptionComponent: MarkdownDescription
                }}
              />
            </Form>
          </Table.Cell>
        </Table.Row>
      </Table.Body>
    </Table>
  );
});
